import {
  atom,
  Atom,
  isEqual,
  uniqueId,
  type TLShape,
  type TLShapeId,
} from "tldraw";
import { AgentInput } from "../../../shared/types/AgentInput";
import { AgentRequest } from "../../../shared/types/AgentRequest";
import { isStudentShape } from "../../utils/studentShapeProtection";
import type { TldrawAgent } from "../TldrawAgent";
import { BaseAgentManager } from "./BaseAgentManager";

function withoutSize(props: object): object {
  const { w: _w, h: _h, ...rest } = props as { w?: unknown; h?: unknown };
  return rest;
}

/** The student's work as a turn captured it. */
export interface SeenStudentWork {
  shapeIds: Set<TLShapeId>;
  /** Latest user edit version per shape; a later edit gets a new one. */
  editVersions: Map<TLShapeId, string>;
  /** Order of the capture; a later capture has a higher number. */
  captureNumber: number;
}

const NOTHING_SEEN: SeenStudentWork = {
  shapeIds: new Set(),
  editVersions: new Map(),
  captureNumber: 0,
};

/**
 * Manages request lifecycle for an agent.
 * Handles the active request and request state management.
 */
export class AgentRequestManager extends BaseAgentManager {
  /**
   * An atom containing the currently active request.
   * This is mainly used to render highlights and other UI elements.
   */
  private $activeRequest: Atom<AgentRequest | null>;

  /**
   * True when the student has added, updated, or removed shapes since the last
   * agent turn. A capture, removal or restore lowers it again when the page
   * is back to what the tutor last saw, or holds no student work at all.
   * Drives the submit controls and, on the gateway, whether a silent tap runs
   * a turn.
   * Only user-sourced store changes set this flag — agent drawing does not.
   */
  private $hasViewportChanged: Atom<boolean>;

  private studentEdits = new Map<TLShapeId, string>();

  /**
   * Latest user edit version per shape, for the flag alone. Unlike
   * `studentEdits` it survives a delete, so undoing the erase of an edited
   * shape brings the edit back with it.
   */
  private userEditVersions = new Map<TLShapeId, string>();

  /**
   * Compared against versions, not `studentEdits` membership: entries are
   * acknowledged only for shapes the blurry-shapes part reports, which leaves
   * widgets and partly visible shapes pending for good.
   */
  private seenStudentWork = NOTHING_SEEN;

  private lastCaptureNumber = 0;

  /** A user create or delete landed in the current store operation. */
  private isPendingWorkRecheckNeeded = false;

  /**
   * A function that cancels the agent's current prompt, if one is active.
   */
  private cancelFn: (() => void) | null = null;

  /**
   * Creates a new request manager for the given agent.
   * Initializes all request-related atoms with default values.
   */
  constructor(agent: TldrawAgent) {
    super(agent);
    this.$activeRequest = atom("activeRequest", null);
    this.$hasViewportChanged = atom("hasViewportChanged", false);

    const isInViewport = (shape: { id: string }) => {
      const viewport = agent.editor.getViewportPageBounds();
      const bounds = agent.editor.getShapeMaskedPageBounds(
        shape as Parameters<typeof agent.editor.getShapeMaskedPageBounds>[0],
      );
      return !!bounds && viewport.collides(bounds);
    };

    const unlistenCreate = agent.editor.sideEffects.registerAfterCreateHandler(
      "shape",
      (_shape, source) => {
        if (source !== "user") return;
        if (agent.getIsActingOnEditor()) return;
        // Judged like a delete: undoing an erase can restore the page the
        // last turn saw, and a new shape still differs from it.
        this.isPendingWorkRecheckNeeded = true;
      },
    );
    const unlistenDelete = agent.editor.sideEffects.registerAfterDeleteHandler(
      "shape",
      (shape, source) => {
        this.studentEdits.delete(shape.id);
        if (source !== "user") return;
        if (agent.getIsActingOnEditor()) return;
        this.isPendingWorkRecheckNeeded = true;
      },
    );
    // tldraw runs after-create/delete once per shape, all against the final
    // page, so an eraser sweep is judged once, when the operation is done.
    const unlistenOperation =
      agent.editor.sideEffects.registerOperationCompleteHandler(() => {
        if (!this.isPendingWorkRecheckNeeded) return;
        this.isPendingWorkRecheckNeeded = false;
        this.$hasViewportChanged.set(this.hasUnsubmittedStudentWork());
      });
    const unlistenChange = agent.editor.sideEffects.registerAfterChangeHandler(
      "shape",
      (prev, next, source) => {
        if (source !== "user") return;
        if (agent.getIsActingOnEditor()) return;
        const isSamePlacement =
          prev.x === next.x &&
          prev.y === next.y &&
          prev.rotation === next.rotation &&
          prev.opacity === next.opacity &&
          prev.parentId === next.parentId;
        // Lock, metadata and stacking updates are bookkeeping, not student work.
        if (isSamePlacement && isEqual(prev.props, next.props)) return;
        // A shape settles its own size once rendered, after the agent's action
        // has ended: tutor shapes, and typeset formulas whoever authored them
        // (a photographed page arrives as the student's math).
        if (
          isSamePlacement &&
          (next.type === "math" || !isStudentShape(next)) &&
          isEqual(withoutSize(prev.props), withoutSize(next.props))
        )
          return;
        const version = uniqueId();
        this.studentEdits.set(next.id, version);
        this.userEditVersions.set(next.id, version);
        if (isInViewport(next)) this.$hasViewportChanged.set(true);
      },
    );

    this.disposables.add(unlistenCreate);
    this.disposables.add(unlistenDelete);
    this.disposables.add(unlistenChange);
    this.disposables.add(unlistenOperation);
  }

  /**
   * Reset the request manager to its initial state.
   * Clears the active request.
   */
  reset(): void {
    this.$activeRequest.set(null);
    this.$hasViewportChanged.set(false);
    this.studentEdits.clear();
    this.userEditVersions.clear();
    this.seenStudentWork = NOTHING_SEEN;
    this.isPendingWorkRecheckNeeded = false;
    this.cancelFn = null;
  }

  hasViewportChanged(): boolean {
    return this.$hasViewportChanged.get();
  }

  /**
   * Input: none. Output: whether the page holds a student shape or a shape
   * with an unacknowledged student edit, such as a widget answer.
   * Edits count only while their shape exists: a snapshot load replaces the
   * store without side effects and leaves their entries behind.
   */
  hasStudentWorkOnPage(): boolean {
    return (
      this.hasPendingStudentEdit() ||
      this.agent.editor.getCurrentPageShapes().some(isStudentShape)
    );
  }

  private hasPendingStudentEdit(): boolean {
    for (const id of this.studentEdits.keys()) {
      if (this.agent.editor.getShape(id)) return true;
    }
    return false;
  }

  private hasEditSinceCapture(): boolean {
    const seen = this.seenStudentWork.editVersions;
    for (const [id, version] of this.userEditVersions) {
      if (seen.get(id) !== version && this.agent.editor.getShape(id)) {
        return true;
      }
    }
    return false;
  }

  /** Output: the student's work as it stands, for a turn about to capture it. */
  captureSeenStudentWork(): SeenStudentWork {
    return {
      shapeIds: this.collectStudentShapeIds(),
      editVersions: new Map(this.userEditVersions),
      captureNumber: ++this.lastCaptureNumber,
    };
  }

  private collectStudentShapeIds(): Set<TLShapeId> {
    return new Set(
      this.agent.editor
        .getCurrentPageShapes()
        .filter(isStudentShape)
        .map((shape) => shape.id),
    );
  }

  /**
   * Student work differs from what the last turn saw. An empty page never
   * does: erasing everything, already-seen work included, leaves nothing to
   * submit.
   */
  private hasUnsubmittedStudentWork(): boolean {
    if (this.hasEditSinceCapture()) return true;
    const current = this.collectStudentShapeIds();
    if (current.size === 0) return false;
    const submitted = this.seenStudentWork.shapeIds;
    if (current.size !== submitted.size) return true;
    for (const id of current) {
      if (!submitted.has(id)) return true;
    }
    return false;
  }

  getStudentEdits(shapes: TLShape[]): Map<TLShapeId, string> {
    return new Map(
      shapes.flatMap((shape) => {
        const edited = this.studentEdits.get(shape.id);
        return edited ? [[shape.id, edited] as const] : [];
      }),
    );
  }

  acknowledgeStudentEdits(edits: Map<TLShapeId, string>): void {
    for (const [id, edit] of edits) {
      // A newer edit made while the request was in flight belongs to the next turn.
      if (this.studentEdits.get(id) === edit) this.studentEdits.delete(id);
    }
  }

  setHasViewportChanged(value: boolean): void {
    this.$hasViewportChanged.set(value);
  }

  /**
   * Input: the work taken when the turn began capturing the page.
   * Mutates: records it as what the turn saw, and lowers the flag unless the
   * student changed something while the capture was rendering. A capture
   * older than the recorded one is ignored: a superseded turn can finish
   * rendering after the turn that replaced it.
   * Fired by: ScreenshotPartUtil, once the capture is done or abandoned.
   */
  markTurnCaptured(seen: SeenStudentWork): void {
    if (seen.captureNumber < this.seenStudentWork.captureNumber) return;
    this.seenStudentWork = seen;
    this.$hasViewportChanged.set(this.hasUnsubmittedStudentWork());
  }

  /**
   * Clear the active request.
   */
  clearActiveRequest() {
    this.$activeRequest.set(null);
  }

  /**
   * Get a full agent request from a partial one by filling in defaults.
   */
  getFullRequestFromInput(input: AgentInput): AgentRequest {
    const activeRequest = this.getActiveRequest();

    return {
      source: input.source ?? "user",
      agentMessages: input.agentMessages ?? [],
      userMessages: input.userMessages ?? [],
      bounds:
        input.bounds ??
        activeRequest?.bounds ??
        this.agent.editor.getViewportPageBounds(),
    };
  }

  /**
   * Set the active request.
   * @param request - The request to set as active, or null to clear.
   */
  setActiveRequest(request: AgentRequest | null) {
    this.$activeRequest.set(request);
  }

  /**
   * Get the active request.
   * @returns The currently active request, or null if none.
   */
  getActiveRequest() {
    return this.$activeRequest.get();
  }

  /**
   * Set the cancel function for the current request.
   * @param fn - A function that cancels the current request, or null to clear.
   */
  setCancelFn(fn: (() => void) | null) {
    this.cancelFn = fn;
  }

  /**
   * Get the cancel function for the current request.
   * @returns The cancel function, or null if none.
   */
  getCancelFn() {
    return this.cancelFn;
  }

  /**
   * Cancel the current request if one is active.
   */
  cancel() {
    this.cancelFn?.();
    this.$activeRequest.set(null);
    this.cancelFn = null;
  }
}
